import React, { useEffect, useMemo } from "react";

import {
  BrokenLinkIcon as BrokenLink,
  CodeIcon as Code,
  LeafIcon as Leaf,
  Skeleton,
  Typography
} from "@packages/ds";
import moment from "moment";
import { useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";

import ErrorBoundary from "Components/ErrorBoundary";
import PageMeta from "Components/PageMeta";
import { MOMENTJS_TIME_FORMAT } from "Constants/constants";
import EnvironmentImpact from "Containers/EnvironmentImpact";
import { Button, Variants } from "ds/Button";
import * as Dialog from "ds/Dialog";
import { Tooltip } from "ds/Tooltip";
import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import useUrls from "Hooks/useUrls";
import getUrl from "Libs/getUrl";
import {
  capitalize,
  httpStatusDisplay,
  checkGoLive,
  roundedNumber
} from "Libs/utils";
import { environmentSelector } from "Reducers/environment";
import {
  deploymentSizingApiEnabledSelector,
  deploymentResourcesSelector
} from "Reducers/environment/deployment";
import { selectFeatureProjectSetupWizardEnabled } from "Reducers/featureFlags/featureFlags.selectors";
import { organizationByDescriptionIdSelector } from "Reducers/organization";
import { gitProjectSelector } from "Reducers/project/project";
import { useDeploymentType } from "Reducers/project/project/hooks/useDeploymentType";
import {
  getRegions,
  regionSelector,
  regionLabelSelector,
  isGreenRegion
} from "Reducers/project/region";
import { openProjectWizard } from "Reducers/project/wizard";
import { setProjectContext } from "Reducers/settingsMenu";
import ConfigureResourcesButton from "src/organization/pages/billing/projectBilling/components/ConfigureResourcesButton";
import LinkPreview from "src/organization/pages/project/components/LinkPreview/LinkPreview";
import { useAppDispatch, useAppSelector } from "Store/hooks";

import * as S from "./Overview.styles";
import EnvironmmentStatusInfo from "./StatusInfo";

const EnvironmentOverview = () => {
  const intl = useIntl();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { environmentId, organizationId, projectId } = useDecodedParams<{
    environmentId: string;
    organizationId: string;
    projectId: string;
  }>();
  const {
    shouldShowDeploymentType,
    hasStagedActivities,
    isAutomaticDeploymentEnabled
  } = useDeploymentType();
  const projectSetupWizardEnabled = useAppSelector(
    selectFeatureProjectSetupWizardEnabled
  );
  const project = useSelectorWithUrlParams(gitProjectSelector);
  const environment = useSelectorWithUrlParams(environmentSelector);
  const organization = useAppSelector(state =>
    organizationByDescriptionIdSelector(state, {
      organizationDescriptionId: organizationId
    })
  );

  const resources = useAppSelector(state =>
    deploymentResourcesSelector(state, {
      organizationId,
      projectId,
      environmentId
    })
  );

  const isSizingApiEnabled = useAppSelector(state =>
    deploymentSizingApiEnabledSelector(state, {
      organizationId,
      projectId,
      environmentId
    })
  );

  const region = useAppSelector(state =>
    regionSelector(state, { regionId: project?.region })
  );
  const regionLabel = useSelectorWithUrlParams(regionLabelSelector);
  const hasProjectRoutes =
    environment?.params?._links["pf:routes"]?.length !== 0;
  const currentDeployment = useAppSelector(({ deployment }) => {
    return deployment?.data?.[organizationId]?.[projectId]?.[environmentId]
      ?.current;
  });

  const isLive = useMemo(
    () => environment?.is_main && checkGoLive(project, organization) === "live",
    [environment, organization, project]
  );
  const envUrl = useMemo(() => {
    if (environment?.has_deployment !== true) return;
    const routes = currentDeployment?.routes as
      | Record<string, { primary: boolean; type: string }>
      | undefined;

    return routes
      ? Object.keys(routes).find(route => routes[route].primary === true) ||
          Object.keys(routes).find(
            route => routes[route].type !== "redirect"
          ) ||
          Object.keys(routes)[0]
      : "";
  }, [currentDeployment, environment?.has_deployment]);

  useEffect(() => {
    dispatch(getRegions());
  }, [dispatch]);

  useEffect(() => {
    moment.updateLocale("en", {
      relativeTime: {
        future: "in %s",
        past: "%s ago",
        s: "%d second",
        ss: "%d seconds",
        m: "%d minute",
        mm: "%d minutes",
        h: "%d hour",
        hh: "%d hours",
        d: "%d day",
        dd: "%d days",
        M: "%d month",
        MM: "%d months",
        y: "%d year",
        yy: "%d years"
      }
    });
  }, []);

  const formatDate = (date: string) => {
    return moment(date).format(MOMENTJS_TIME_FORMAT);
  };

  const httpAccess = useMemo(
    () => httpStatusDisplay(environment),
    [environment]
  );

  const mainEnv = useSelectorWithUrlParams(environmentSelector, {
    environmentId: project?.default_branch
  });

  const encodedEnvironmentId = environment
    ? encodeURIComponent(environment?.id)
    : undefined;

  const [resourcesUrl] = useUrls(
    "organization.project.environment.configureResources",
    {
      environmentId: environment?.id || "",
      location: location.pathname + location.search,
      queryParams: ""
    }
  );

  const domainsUrl =
    mainEnv?.id === environment?.id
      ? `/${organizationId}/${projectId}/-/settings/domains`
      : `/${organizationId}/${projectId}/${encodedEnvironmentId}/settings/domains`;

  const navigateToSettingsDomains = (e: React.MouseEvent) => {
    e.preventDefault();
    dispatch(setProjectContext());
    navigate(domainsUrl);
  };

  const openWizard = () => {
    dispatch(openProjectWizard({ organizationId, projectId }));
  };

  if (!environment) return null;
  const deploymentState = environment.deployment_state || { crons: undefined };
  const { crons } = deploymentState;
  const { status: deploymentStateStatus, enabled: deploymentStateEnabled } =
    crons || {};

  const isEnvPaused = deploymentStateStatus === "paused";

  const hasEditPermission = environment.hasLink("#edit");
  const envSettingsUrl = getUrl({
    key: "organization.project.environment.settings",
    props: {
      organizationId,
      projectId,
      environmentId: environmentId,
      location: location.pathname + location.search
    }
  });

  const deploymentLinks = hasEditPermission
    ? [
        {
          to: envSettingsUrl,
          text: intl.formatMessage({
            id: "environment.overview.deployment_type.settings"
          }),
          variant: "outline" as Variants
        }
      ]
    : undefined;

  const isGreen = isGreenRegion(region);

  const resourceSkeleton = (
    <S.LayoutSkeleton synchronize>
      <S.Row>
        <Skeleton.Square height="80%" />
      </S.Row>
    </S.LayoutSkeleton>
  );

  return (
    <S.Layout>
      <S.TitleWrapper>
        <S.Header>
          <PageMeta title={`${environment.title} | ${project?.title}`} />
          <S.TitleIconWrapper>
            <S.Title tag="h2" id="environment-info-heading">
              {environment.title}
            </S.Title>
            {shouldShowDeploymentType &&
              hasStagedActivities &&
              !isAutomaticDeploymentEnabled && (
                <Tooltip
                  tooltip={intl.formatMessage({
                    id: "environment.overview.deployment_type.info"
                  })}
                  align="start"
                  alignOffset={2}
                  analyticId="environment.overview.deployment_type.info"
                >
                  <S.Staged
                    data-testid="staged_activity"
                    stroke="var(--color-gold-600)"
                  />
                </Tooltip>
              )}
          </S.TitleIconWrapper>
        </S.Header>
        <S.EnvLiveWrapper>
          <S.Environment>
            {environment.type
              ? capitalize(environment.type)
              : !environment.is_main && "Development"}
          </S.Environment>
          {isLive && <S.Live>{intl.formatMessage({ id: "live" })}</S.Live>}
        </S.EnvLiveWrapper>
      </S.TitleWrapper>
      <S.IdRegion>
        {isGreen && (
          <Dialog.Root>
            <Dialog.Trigger analyticId="environmentImpact">
              <S.IconControlRegion aria-label="leaf">
                <Leaf />
              </S.IconControlRegion>
            </Dialog.Trigger>
            <Dialog.Content>
              <S.ContentWrapper>
                <EnvironmentImpact inModal={true} regionId={region?.id} />
              </S.ContentWrapper>
            </Dialog.Content>
          </Dialog.Root>
        )}

        <Typography.BodyText
          size="extraSmall"
          weight="semiBold"
          $color="granite"
        >
          {regionLabel && `${regionLabel.title} ${regionLabel.suffix}`}
          {region?.provider?.name && ` - ${region.provider.name}`},{" "}
          {project?.id}
        </Typography.BodyText>
      </S.IdRegion>

      {!project?.default_domain && projectSetupWizardEnabled && (
        <S.Wizard>
          <Button
            content="mixed"
            onClick={openWizard}
            analyticId="project.overview.wizard"
          >
            <Code />
            {intl.formatMessage({ id: "project.overview.wizard" })}
          </Button>
        </S.Wizard>
      )}

      <ErrorBoundary>
        <S.GridWrapper>
          <S.ItemWrapper>
            <S.Time aria-labelledby="environment-overview-activity">
              {formatDate(environment.last_active_at)}
            </S.Time>
            <S.Label id="environment-overview-activity">
              {intl.formatMessage({ id: "last_activity" })}
            </S.Label>
          </S.ItemWrapper>

          <S.ItemWrapper className="environment-backup">
            {environment?.last_backup_at ? (
              <S.Time aria-labelledby="environment-overview-backup">
                {formatDate(environment.last_backup_at)}
              </S.Time>
            ) : (
              <S.Time
                className="no-backup"
                aria-labelledby="environment-overview-backup"
              >
                {intl.formatMessage({ id: "no_backup" })}
              </S.Time>
            )}
            <S.Label id="environment-overview-backup">
              {intl.formatMessage({ id: "last_backup" })}
            </S.Label>
          </S.ItemWrapper>

          {typeof isSizingApiEnabled === "undefined" && (
            <>
              {resourceSkeleton}
              {resourceSkeleton}
            </>
          )}

          {isSizingApiEnabled && resources && (
            <>
              <S.ItemWrapper>
                <S.Value aria-labelledby="environment-overview-is-hidden-from-search-engines">
                  {intl.formatMessage(
                    {
                      id: "project.details.double_value"
                    },
                    {
                      value: `${roundedNumber(resources.cpu, 1)} CPU,`,
                      value2: `${roundedNumber(resources.ram, 1)} GB RAM`,
                      separator: () => " "
                    }
                  )}
                </S.Value>
                <S.Label id="environment-overview-is-hidden-from-search-engines">
                  {intl.formatMessage({ id: "project.details.resources" })}
                </S.Label>
              </S.ItemWrapper>

              <S.ItemWrapper>
                <S.Value aria-labelledby="environment-overview-status">
                  {intl.formatMessage(
                    {
                      id: "project.details.double_value"
                    },
                    {
                      value: resources.disk,
                      separator: () => " ",
                      value2: "GB"
                    }
                  )}
                  {environment?.deployment_state && (
                    <S.ConfigureResourcesButtonWrapper>
                      <ConfigureResourcesButton
                        environmentConfigUrl={resourcesUrl}
                      />
                    </S.ConfigureResourcesButtonWrapper>
                  )}
                </S.Value>
                <S.Label id="environment-overview-status">
                  {intl.formatMessage({ id: "project.details.disk" })}
                </S.Label>
              </S.ItemWrapper>
            </>
          )}

          <S.ItemWrapper>
            <S.HttpAccessStatus
              status={httpAccess ? "enabled" : "disabled"}
              aria-labelledby="environment-overview-http-access"
              isProduction={environment.type === "production"}
            >
              {intl.formatMessage({
                id: httpAccess ? "enabled" : "disabled"
              })}
            </S.HttpAccessStatus>
            <S.Label id="environment-overview-http-access">
              {intl.formatMessage({ id: "http_access" })}
            </S.Label>
          </S.ItemWrapper>

          <S.ItemWrapper>
            <S.Value aria-labelledby="environment-overview-machine-name">
              {environment.machine_name}
            </S.Value>
            <S.Label id="environment-overview-machine-name">
              {intl.formatMessage({ id: "machine_name" })}
            </S.Label>
          </S.ItemWrapper>

          <S.ItemWrapper>
            <S.Status
              status={environment?.status}
              aria-labelledby="environment-overview-status"
            >
              {capitalize(
                intl.formatMessage({
                  id: `environment.status.${environment?.status}`
                })
              )}
            </S.Status>
            <S.Label id="environment-overview-status">
              {capitalize(intl.formatMessage({ id: "status" }))}&nbsp;
              <EnvironmmentStatusInfo
                align="start"
                analyticId="status.dialog.edit"
                title={capitalize(intl.formatMessage({ id: "status" }))}
                description={intl.formatMessage({ id: "status.dialog.edit" })}
                links={[
                  {
                    text: intl.formatMessage({ id: "view_settings" }),
                    to: `/${organizationId}/${projectId}/${encodeURIComponent(
                      environmentId
                    )}/settings`,
                    variant: "outline"
                  },
                  {
                    text: intl.formatMessage({ id: "learnmore" }),
                    href: intl.formatMessage({
                      id: isEnvPaused
                        ? "links.documentation.paused_environment"
                        : "links.documentation.inactive_environment"
                    }),
                    target: "_blank",
                    variant: "link"
                  }
                ]}
              />
            </S.Label>
          </S.ItemWrapper>

          <S.ItemWrapper>
            <S.Value aria-labelledby="environment-overview-is-hidden-from-search-engines">
              {intl.formatMessage({
                id: environment.restrict_robots ? "hidden" : "visible"
              })}
            </S.Value>
            <S.Label id="environment-overview-is-hidden-from-search-engines">
              {intl.formatMessage({ id: "search_engine_index" })}
            </S.Label>
          </S.ItemWrapper>

          {deploymentStateEnabled && (
            <S.ItemWrapper>
              <S.Value aria-labelledby="environment-overview-crons">
                {intl.formatMessage({
                  id: `environment.overview.crons.status.${deploymentStateStatus}`
                })}{" "}
              </S.Value>
              <S.Label id="environment-overview-crons">
                {intl.formatMessage({ id: "environment.overview.crons" })}
                &nbsp;
                {isEnvPaused && (
                  <EnvironmmentStatusInfo
                    analyticId="environment.overview.crons.sleeping"
                    title={intl.formatMessage({ id: "crons" })}
                    description={intl.formatMessage({
                      id: "environment.overview.crons.sleeping"
                    })}
                    links={[
                      {
                        href: intl.formatMessage({
                          id: "links.documentation.paused_crons"
                        }),
                        target: "_blank",
                        text: intl.formatMessage({ id: "learnmore" })
                      }
                    ]}
                  />
                )}
              </S.Label>
            </S.ItemWrapper>
          )}
          {shouldShowDeploymentType && (
            <S.ItemWrapper>
              {" "}
              <S.Value aria-labelledby="environment-overview-deployment">
                {intl.formatMessage({
                  id: isAutomaticDeploymentEnabled
                    ? "environment.overview.deployment_type.enabled"
                    : "environment.overview.deployment_type.disabled"
                })}{" "}
              </S.Value>
              <S.Label id="environment-overview-deployment">
                {intl.formatMessage({
                  id: "environment.overview.deployment_type"
                })}

                <EnvironmmentStatusInfo
                  analyticId="environment.overview.deployment_type.title"
                  title={intl.formatMessage({
                    id: "environment.overview.deployment_type.title"
                  })}
                  description={intl.formatMessage(
                    {
                      id: "environment.overview.deployment_type.description"
                    },
                    { br: <br /> }
                  )}
                  tooltip={intl.formatMessage({
                    id: "environment.overview.deployment_type.no_access"
                  })}
                  tag={!hasEditPermission}
                  links={deploymentLinks}
                />
              </S.Label>
            </S.ItemWrapper>
          )}
        </S.GridWrapper>

        {environment.status === "inactive" ? (
          <S.NoUrl>
            <BrokenLink />
            <S.InactiveMessage>
              {intl.formatMessage({ id: "environment.status.inactive.title" })}
            </S.InactiveMessage>
            <EnvironmmentStatusInfo
              title={intl.formatMessage({
                id: "environment.status.inactive.url"
              })}
              analyticId="environment.status.inactive.update"
              description={intl.formatMessage({
                id: "environment.status.inactive.update"
              })}
              links={[
                {
                  to: `/${organizationId}/${projectId}/${encodeURIComponent(
                    environmentId
                  )}/settings`,
                  text: intl.formatMessage({ id: "view_settings" })
                },
                {
                  href: intl.formatMessage({
                    id: "links.documentation.inactive_environment"
                  }),
                  target: "_blank",
                  text: intl.formatMessage({ id: "learnmore" })
                }
              ]}
            />
          </S.NoUrl>
        ) : (
          <LinkPreview
            hasProjectRoutes={hasProjectRoutes}
            domainsUrl={domainsUrl}
            onDomainLinkClick={navigateToSettingsDomains}
            isInactive={false}
            mainUrl={envUrl}
            organizationId={organizationId}
            projectId={projectId}
            environmentId={environmentId}
            isEnvironment={true}
          />
        )}
      </ErrorBoundary>
    </S.Layout>
  );
};

export default EnvironmentOverview;
